import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, X, Loader2, AlertTriangle, Phone } from "lucide-react";

const CATEGORIES = [
    { key: "conduct", label: "Inappropriate conduct" },
    { key: "harassment", label: "Harassment / threat" },
    { key: "unsafe", label: "Unsafe situation" },
    { key: "theft", label: "Suspected theft" },
    { key: "injury", label: "Injury / medical" },
    { key: "other", label: "Something else" },
];

export function SafetyReport({ bookingId, className }) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("report"); // report | emergency
    const [category, setCategory] = useState("conduct");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!description.trim() && mode === "report") return toast.error("Please describe the concern");
        setBusy(true);
        try {
            const url = mode === "emergency" ? "/safety/emergency" : "/safety/report";
            const body = mode === "emergency"
                ? { booking_id: bookingId, category: "emergency", severity: "emergency", description: description.trim() || "Emergency help requested" }
                : { booking_id: bookingId, category, severity: "medium", description: description.trim() };
            const { data } = await api.post(url, body);
            toast.success(`Reported · ${data.case_id}. HOMIIE Support will reach out.`);
            setOpen(false); setDescription("");
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <button data-testid="safety-report-btn" onClick={() => { setMode("report"); setOpen(true); }} className={className || "flex w-full items-center justify-center gap-2 rounded-full border border-red-200 py-3 text-sm font-bold text-red-600 transition-colors hover:bg-red-50"}>
                <ShieldAlert size={16} /> Report a safety concern
            </button>

            {open && (
                <div className="fixed inset-0 z-[80] flex items-end justify-center bg-navy/50" onClick={() => setOpen(false)}>
                    <div className="w-full max-w-md rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()} data-testid="safety-sheet">
                        <div className="mb-2 flex items-center justify-between">
                            <h3 className="font-heading text-lg font-extrabold text-navy">Safety & help</h3>
                            <button data-testid="safety-sheet-close" onClick={() => setOpen(false)} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button>
                        </div>

                        <button data-testid="safety-emergency-btn" onClick={() => setMode("emergency")} className={`flex w-full items-center gap-3 rounded-2xl border-2 p-3.5 text-left transition-colors ${mode === "emergency" ? "border-red-500 bg-red-50" : "border-red-200"}`}>
                            <AlertTriangle size={22} className="text-red-600" />
                            <div><p className="font-heading text-sm font-extrabold text-red-600">Emergency help</p><p className="text-xs text-navy/55">Immediate danger or medical emergency</p></div>
                        </button>
                        {mode === "emergency" && (
                            <a href="tel:112" className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-red-600 py-3 text-sm font-bold text-white"><Phone size={15} /> Call 112 (emergency services)</a>
                        )}

                        {mode === "report" && (
                            <div className="mt-4">
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-navy/45">What's the concern?</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {CATEGORIES.map((c) => (
                                        <button key={c.key} data-testid={`safety-cat-${c.key}`} onClick={() => setCategory(c.key)} className={`rounded-xl border px-3 py-2.5 text-xs font-bold ${category === c.key ? "border-red-400 bg-red-50 text-red-600" : "border-navy/15 text-navy/60"}`}>{c.label}</button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <textarea data-testid="safety-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder={mode === "emergency" ? "Briefly describe what's happening (optional)" : "Describe what happened…"} className="mt-3 w-full rounded-xl border border-navy/15 px-3 py-3 text-sm outline-none focus:border-red-400" />

                        <button data-testid="safety-submit-btn" disabled={busy} onClick={submit} className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold text-white disabled:opacity-60 ${mode === "emergency" ? "bg-red-600" : "bg-navy"}`}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert size={16} />} {mode === "emergency" ? "SEND EMERGENCY ALERT" : "SUBMIT REPORT"}
                        </button>
                        <p className="mt-3 text-[11px] text-navy/40">HOMIIE logs your case and Support will contact you. We never share your report beyond authorised staff. For life-threatening danger always call 112 first.</p>
                    </div>
                </div>
            )}
        </>
    );
}
